<template>
    <section class="content">
        <section class="content-header">
            <div class="container-fluid">
                <div class="row mb-2">
                    <div class="col-sm-6">
                        <h1>Auditoría de Inventario</h1>
                    </div>
                    <div class="col-sm-6">
                        <ol class="breadcrumb float-sm-right">
                            <li class="breadcrumb-item"><a href="/">Home</a></li>
                            <li class="breadcrumb-item"><a href="/inventario">Inventario</a></li>
                            <li class="breadcrumb-item active">Realizar Auditoría</li>
                        </ol>
                    </div>
                </div>
            </div>
        </section>

        <div class="container-fluid">
            <div class="card">
                <div class="card-header">
                    <h3 class="card-title">Comparación de Stock Físico vs Sistema</h3>
                    <div class="card-tools">
                        <button v-if="!cargando && reporte.length > 0" @click="finalizarAuditoria"
                            class="btn btn-success btn-sm">
                            <i class="fas fa-check-circle"></i> Finalizar y Generar Reporte
                        </button>
                    </div>
                </div>
                <div class="card-body p-0">
                    <div v-if="cargando" class="text-center p-5">
                        <div class="spinner-border text-primary" role="status"></div>
                        <p class="mt-2">Obteniendo datos del sistema...</p>
                    </div>

                    <div v-else class="table-responsive">
                        <table class="table table-hover table-striped mb-0">
                            <thead>
                                <tr>
                                    <th>Producto</th>
                                    <th>Almacén</th>
                                    <th class="text-center">Stock Sistema</th>
                                    <th class="text-center">¿Coincide?</th>
                                    <th style="width: 200px;">Cantidad Física</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in reporte" :key="index">
                                    <td>
                                        <strong>{{ item.codigo }}</strong><br>
                                        <small>{{ item.producto }}</small>
                                    </td>
                                    <td>{{ item.almacen }}</td>
                                    <td class="text-center">
                                        <span class="badge badge-info">{{ item.stock_actual }} UN</span>
                                    </td>
                                    <td class="text-center">
                                        <div class="custom-control custom-switch">
                                            <input type="checkbox" class="custom-control-input"
                                                :id="'customSwitch' + index" v-model="item.coincide"
                                                @change="handleCoincideChange(index)">
                                            <label class="custom-control-label" :for="'customSwitch' + index">
                                                {{ item.coincide ? 'Sí' : 'No' }}
                                            </label>
                                        </div>
                                    </td>
                                    <td>
                                        <input type="number" class="form-control form-control-sm"
                                            v-model.number="item.cantidad_fisica" :disabled="item.coincide"
                                            :class="{ 'is-invalid': !item.coincide && (item.cantidad_fisica === null || item.cantidad_fisica < 0) }">
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="card-footer" v-if="!cargando">
                    <small class="text-muted">Nota: Al finalizar, el sistema ajustará automáticamente el stock a la
                        cantidad física ingresada.</small>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import api from '@/services/api';
import Swal from 'sweetalert2';

const reporte = ref([]);
const cargando = ref(true);

const fetchAuditoriaInicial = async () => {
    try {
        cargando.value = true;
        const response = await api.post('/inventario/auditar');
        // Mapeamos la respuesta para agregar campos de control local
        reporte.value = response.data.map(item => ({
            ...item,
            coincide: true,
            cantidad_fisica: item.stock_actual
        }));
    } catch (error) {
        console.error("Error al iniciar auditoría:", error);
        Swal.fire('Error', 'No se pudo obtener el reporte de auditoría.', 'error');
    } finally {
        cargando.value = false;
    }
};

const handleCoincideChange = (index) => {
    if (reporte.value[index].coincide) {
        reporte.value[index].cantidad_fisica = reporte.value[index].stock_actual;
    }
};

const finalizarAuditoria = async () => {
    // Validar que todos los campos tengan valores válidos
    const invalidos = reporte.value.some(item => !item.coincide && (item.cantidad_fisica === null || item.cantidad_fisica < 0));

    if (invalidos) {
        Swal.fire('Atención', 'Por favor ingrese cantidades físicas válidas para los productos que no coinciden.', 'warning');
        return;
    }

    const result = await Swal.fire({
        title: '¿Finalizar Auditoría?',
        text: "Se generará el reporte final y se ajustará el stock en el sistema.",
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, finalizar',
        cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
        try {
            Swal.fire({ title: 'Procesando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

            const response = await api.post('/inventario/finalizar-auditoria', {
                resultados: reporte.value.map(item => ({
                    inventario_id: item.id,
                    cantidad_fisica: item.cantidad_fisica
                }))
            });

            const byteCharacters = atob(response.data.pdf);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', response.data.nombre_archivo);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            Swal.fire('Completado', 'Auditoría finalizada y stock actualizado.', 'success')
                .then(() => { window.location.href = '/inventario'; });

        } catch (error) {
            console.error("Error al finalizar auditoría:", error);
            Swal.fire('Error', 'Ocurrió un error al procesar la auditoría final.', 'error');
        }
    }
};

onMounted(fetchAuditoriaInicial);
</script>

<style scoped>
.table td {
    vertical-align: middle;
}
</style>
